import { SkillData, documentEntryHasLinkedGuide } from '@skill-trees/shared';
import { TreeInteractor } from '../tree-domain/TreeInteractor';
import { buildTreeViewUrl } from '../routing/routing';
import { SkillMarkdownRenderer, type SkillDescription } from './SkillMarkdownRenderer';
import type { MarkdownTocEntry } from './markdownTableOfContents';
import * as skillPanelEditorPolicy from './skillPanelEditorPolicy';

export type { MarkdownTocEntry, SkillDescription };

/**
 * Browser storage abstraction for the in-progress editor text backup (localStorage in
 * production, inject in-memory for tests). This "local backup" layer is distinct from
 * the server-side draft guide files: unsaved editor text does NOT count as a draft.
 */
export interface DraftStorage {
    getItem(key: string): string | null;
    setItem(key: string, value: string): void;
    removeItem(key: string): void;
}

export interface SkillLocalBackupRecord {
    content: string;
    timestamp: number;
}

/** Whether the given leaf has a linked markdown guide in `skillTreeData.documents`. */
function skillLeafHasLinkedGuide(skillTreeData: SkillData, skillId: string | null): boolean {
    if (!skillId) {
        return false;
    }
    const entry = skillTreeData.documents.find((d) => d.nodeId === skillId);
    return entry !== undefined && documentEntryHasLinkedGuide(entry);
}

function createDefaultDraftStorage(): DraftStorage {
    if (typeof window !== 'undefined' && window.localStorage) {
        return {
            getItem: (key) => window.localStorage.getItem(key),
            setItem: (key, value) => window.localStorage.setItem(key, value),
            removeItem: (key) => window.localStorage.removeItem(key),
        };
    }
    const map = new Map<string, string>();
    return {
        getItem: (key) => map.get(key) ?? null,
        setItem: (key, value) => map.set(key, value),
        removeItem: (key) => map.delete(key),
    };
}

export interface SkillPanelState {
    skill: SkillDescription | null;
    loading: boolean;
    isEditing: boolean;
    markdownContent: string;
    originalMarkdown: string;
    isSaving: boolean;
    isMarkdownLoaded: boolean;
    /** In-progress editor text differs from the last saved markdown (localStorage backup layer). */
    hasLocalBackup: boolean;
    /**
     * A server-side draft copy of this guide exists (saved but not yet published).
     * Drives the panel menu's DRAFT indicator and Publish item.
     */
    isGuideDraft: boolean;
    /** A guide publish request is in flight (menu shows "Publishing…" and re-clicks are rejected). */
    isPublishingGuide: boolean;
}

/** Outcome of {@link SkillPanelInteractor.startEdit} (no throws for expected preconditions). */
export type StartEditResult =
    | { ok: true }
    | { ok: false; reason: 'no_skill' | 'markdown_not_loaded' | 'document_load_failed' };

/** Outcome of {@link SkillPanelInteractor.save} (no throws for expected preconditions). */
export type SaveResult =
    | { ok: true }
    | {
          ok: false;
          reason: 'no_skill' | 'already_saving' | 'data_not_loaded' | 'markdown_not_loaded' | 'persist_failed';
      };

/** Outcome of {@link SkillPanelInteractor.publishGuide} (no throws for expected preconditions). */
export type PublishGuideResult =
    | { ok: true }
    | { ok: false; reason: 'already_publishing' }
    | { ok: false; reason: 'publish_failed'; error: string };

export class SkillPanelInteractor {
    private state: SkillPanelState;
    private updateCallback?: (state: SkillPanelState) => void;
    private currentSkillId: string | null = null;
    private readonly markdownRenderer: SkillMarkdownRenderer;
    private readonly draftStorage: DraftStorage;

    /**
     * `linkOrigin` is the base for shareable links: a string is used verbatim,
     * `undefined` falls back to the current page's origin, and `null` means this
     * tree has no public home — a deployed artifact — so link affordances are off.
     */
    constructor(
        private readonly treeInteractor: TreeInteractor,
        private skillTreeData: SkillData,
        private readonly linkOrigin?: string | null,
        draftStorage?: DraftStorage,
    ) {
        this.markdownRenderer = new SkillMarkdownRenderer(treeInteractor.getTreeId(), {
            sectionCopyLinks: linkOrigin !== null,
        });
        this.draftStorage = draftStorage ?? createDefaultDraftStorage();
        this.state = {
            skill: null,
            loading: false,
            isEditing: false,
            markdownContent: '',
            originalMarkdown: '',
            isSaving: false,
            isMarkdownLoaded: false,
            hasLocalBackup: false,
            isGuideDraft: false,
            isPublishingGuide: false,
        };
    }

    setUpdateCallback(callback: (state: SkillPanelState) => void): void {
        this.updateCallback = callback;
    }

    getState(): SkillPanelState {
        return { ...this.state };
    }

    updateSkillTreeData(skillTreeData: SkillData): void {
        this.skillTreeData = skillTreeData;
    }

    private updateState(updates: Partial<SkillPanelState>): void {
        this.state = { ...this.state, ...updates };
        if (this.updateCallback) {
            this.updateCallback(this.state);
        }
    }

    /**
     * Loads skill description for a given skillId
     */
    async loadSkillDescription(skillId: string, bustCache: boolean = false): Promise<void> {
        // No selection → clear and stop
        if (!skillId) {
            this.currentSkillId = null;
            this.updateState({
                skill: null,
                loading: false,
                isEditing: false,
                isMarkdownLoaded: false,
                hasLocalBackup: false,
                isGuideDraft: false,
            });
            return;
        }

        // If we already have this skill loaded and not busting cache, don't reload
        if (!bustCache && this.currentSkillId === skillId && this.state.skill) {
            return;
        }

        // Ensure there is a mapping for the selected node before fetching
        const documentEntry = this.skillTreeData.documents.find(doc => doc.nodeId === skillId);
        if (!documentEntry || !documentEntryHasLinkedGuide(documentEntry)) {
            // No linked guide (absent entry, optional leaf, or new node) — do not call storage
            this.currentSkillId = skillId;
            this.updateState({
                skill: {
                    title: skillId,
                    html: '<p>This skill leaf has no linked guide.</p>',
                    tableOfContents: [],
                },
                loading: false,
                isEditing: false,
                isMarkdownLoaded: false,
                hasLocalBackup: false,
                isGuideDraft: false,
            });
            return;
        }

        // Only show loading if we're loading a different skill
        if (this.currentSkillId !== skillId) {
            this.updateState({ loading: true, isMarkdownLoaded: false, hasLocalBackup: false, isGuideDraft: false });
        }
        this.currentSkillId = skillId;

        try {
            const state = await this.loadDocumentState(skillId);
            if (this.currentSkillId !== skillId) {
                return;
            }
            this.updateState(state);
        } catch (error) {
            if (this.currentSkillId !== skillId) {
                return;
            }
            this.updateState({
                skill: {
                    title: skillId,
                    html: '<p>Error loading description.</p>',
                    tableOfContents: [],
                },
                loading: false,
                isMarkdownLoaded: false,
                hasLocalBackup: false,
                isGuideDraft: false,
            });
        }
    }

    private async loadDocumentState(skillId: string) {
        const currentData = this.treeInteractor.getData();
        const currentDocumentEntry = currentData.documents.find(doc => doc.nodeId === skillId);

        if (!currentDocumentEntry || !documentEntryHasLinkedGuide(currentDocumentEntry)) {
            return {
                skill: {
                    title: skillId,
                    html: '<p>This skill leaf has no linked guide.</p>',
                    tableOfContents: [],
                },
                loading: false,
                isMarkdownLoaded: false,
                hasLocalBackup: false,
            };
        } else {
            const result = await this.treeInteractor.loadDocument(skillId);
            const document = result.success ? result.data : null;
            const markdown = document?.content ?? null;
            if (!markdown) {
                return {
                    skill: {
                        title: skillId,
                        html: '<p>Document not found.</p>',
                        tableOfContents: [],
                    },
                    loading: false,
                    isMarkdownLoaded: false,
                    hasLocalBackup: false,
                    isGuideDraft: false,
                };
            } else {
                const skill = this.markdownRenderer.convertToSkillDescription(markdown, skillId);
                return {
                    skill,
                    loading: false,
                    isMarkdownLoaded: true,
                    hasLocalBackup: false,
                    isGuideDraft: document?.isDraft === true,
                };
            }
        }
    }

    /**
     * Starts editing mode for the current skill
     */
    async startEdit(skillId: string): Promise<StartEditResult> {
        if (!skillId) {
            return { ok: false, reason: 'no_skill' };
        }

        // If we're already editing, don't reload the document
        if (this.state.isEditing) {
            return { ok: true };
        }

        if (!this.state.isMarkdownLoaded) {
            return { ok: false, reason: 'markdown_not_loaded' };
        }

        const treeId = this.treeInteractor.getTreeId();
        try {
            this.updateState({ isEditing: true, isMarkdownLoaded: false });
            const result = await this.treeInteractor.loadDocument(skillId);
            const serverMarkdown = result.success ? result.data.content ?? '' : '';
            const stored = this.loadLocalBackup(treeId, skillId);
            let markdownContent = serverMarkdown;
            if (stored && stored.content === serverMarkdown) {
                this.clearLocalBackup(treeId, skillId);
            } else if (stored && stored.content !== serverMarkdown) {
                markdownContent = stored.content;
            }
            const hasLocalBackup = markdownContent !== serverMarkdown;
            this.updateState({
                markdownContent,
                originalMarkdown: serverMarkdown,
                isEditing: true,
                isMarkdownLoaded: true,
                hasLocalBackup,
                ...(result.success ? { isGuideDraft: result.data.isDraft } : {}),
            });
            return { ok: true };
        } catch (error) {
            console.error('Failed to load document for editing:', error);
            this.updateState({
                markdownContent: '',
                originalMarkdown: '',
                isEditing: false,
                isMarkdownLoaded: true,
                hasLocalBackup: false,
            });
            return { ok: false, reason: 'document_load_failed' };
        }
    }

    /**
     * Updates the markdown content while editing
     */
    updateMarkdownContent(content: string): void {
        const hasLocalBackup = content !== this.state.originalMarkdown;
        this.updateState({ markdownContent: content, hasLocalBackup });
    }

    private localBackupStorageKey(treeId: string, skillId: string): string {
        return `skill-draft:${treeId}:${skillId}`;
    }

    loadLocalBackup(treeId: string, skillId: string): SkillLocalBackupRecord | null {
        const raw = this.draftStorage.getItem(this.localBackupStorageKey(treeId, skillId));
        if (!raw) {
            return null;
        }
        try {
            const parsed = JSON.parse(raw) as Partial<SkillLocalBackupRecord>;
            if (typeof parsed.content === 'string' && typeof parsed.timestamp === 'number') {
                return { content: parsed.content, timestamp: parsed.timestamp };
            }
        } catch {
            /* ignore */
        }
        return null;
    }

    saveLocalBackup(treeId: string, skillId: string, content: string): void {
        if (!skillId) {
            return;
        }
        const baseline = this.state.originalMarkdown;
        if (content === baseline) {
            this.clearLocalBackup(treeId, skillId);
            this.updateState({ hasLocalBackup: false });
            return;
        }
        const payload: SkillLocalBackupRecord = { content, timestamp: Date.now() };
        this.draftStorage.setItem(this.localBackupStorageKey(treeId, skillId), JSON.stringify(payload));
        this.updateState({ hasLocalBackup: true });
    }

    clearLocalBackup(treeId: string, skillId: string): void {
        if (!skillId) {
            return;
        }
        this.draftStorage.removeItem(this.localBackupStorageKey(treeId, skillId));
    }

    /**
     * Saves the current markdown content
     */
    async save(skillId: string): Promise<SaveResult> {
        if (!skillId) {
            return { ok: false, reason: 'no_skill' };
        }
        if (this.state.isSaving) {
            return { ok: false, reason: 'already_saving' };
        }

        if (!this.treeInteractor.getIsDataLoaded()) {
            return { ok: false, reason: 'data_not_loaded' };
        }

        if (!this.state.isMarkdownLoaded) {
            return { ok: false, reason: 'markdown_not_loaded' };
        }

        try {
            console.log('Saving document for skillId:', skillId);
            console.log('Content to save:', this.state.markdownContent);

            this.updateState({ isSaving: true });

            const markdownToSave = this.state.markdownContent;
            // Immediately convert the edited markdown to HTML and update the UI
            const updatedSkill = this.markdownRenderer.convertToSkillDescription(markdownToSave, skillId);
            this.updateState({
                skill: updatedSkill,
                isEditing: false,
                markdownContent: '',
                originalMarkdown: '',
                hasLocalBackup: false,
            });

            await this.treeInteractor.saveDocument(skillId, markdownToSave);
            this.clearLocalBackup(this.treeInteractor.getTreeId(), skillId);
            // The save landed as a server-side draft guide — only now does the guide
            // enter draft state (unsaved editor text never counts).
            this.updateState({ isGuideDraft: true });
            console.log('Document saved successfully');
            return { ok: true };
        } catch (error) {
            console.error('Failed to save document:', error);
            return { ok: false, reason: 'persist_failed' };
        } finally {
            this.updateState({ isSaving: false });
        }
    }

    /**
     * Publishes the current skill's draft guide (menu action). Clears the DRAFT
     * indicator on success; the tree-structure draft (if any) stays unpublished.
     * Re-entrant calls while a publish is in flight are rejected — without this,
     * the second request can race the first one's draft-file cleanup and surface
     * a spurious "no draft guide to publish" error.
     */
    async publishGuide(skillId: string): Promise<PublishGuideResult> {
        if (this.state.isPublishingGuide) {
            return { ok: false, reason: 'already_publishing' };
        }
        this.updateState({ isPublishingGuide: true });
        try {
            const result = await this.treeInteractor.publishGuide(skillId);
            if (!result.success) {
                return { ok: false, reason: 'publish_failed', error: result.error };
            }
            this.updateState({ isGuideDraft: false });
            return { ok: true };
        } finally {
            this.updateState({ isPublishingGuide: false });
        }
    }

    /**
     * A tree publish completed: publish reconciliation promoted and deleted every
     * draft guide, so the loaded guide (if any) is no longer a draft. Clears the
     * DRAFT badge that would otherwise stay stale until the next document load.
     */
    handleTreePublished(): void {
        if (this.state.isGuideDraft) {
            this.updateState({ isGuideDraft: false });
        }
    }

    /**
     * Whether the panel menu should offer Publish for this skill: the guide must be
     * in draft state, and the leaf must already exist in the published tree (a leaf
     * added only in the tree draft cannot have its guide published ahead of the tree).
     * Both inputs are local — no network calls from menu-rendering code.
     */
    canPublishGuide(skillId: string | null): boolean {
        if (!skillId) {
            return false;
        }
        return this.state.isGuideDraft && this.treeInteractor.isNodeInPublishedTree(skillId);
    }

    /**
     * Closes edit mode without saving
     */
    closeEdit(): void {
        this.updateState({
            isEditing: false,
            markdownContent: '',
            originalMarkdown: '',
            hasLocalBackup: false,
        });
    }

    cancelEdit(skillId: string): void {
        if (skillId) {
            this.clearLocalBackup(this.treeInteractor.getTreeId(), skillId);
        }
        this.closeEdit();
    }

    /**
     * Resets editing state when skill changes
     */
    resetEditOnSkillChange(skillId: string | null): void {
        if (skillId && this.state.isEditing) {
            this.updateState({
                isEditing: false,
                markdownContent: '',
                originalMarkdown: '',
                isMarkdownLoaded: false,
                hasLocalBackup: false,
            });
        }
    }

    /**
     * Gets parent skills for a given skillId
     */
    getParentSkills(skillId: string | null): Array<{ id: string; name: string }> {
        if (!skillId) return [];
        return this.skillTreeData.links
            .filter(link => link.target === skillId)
            .map(link => this.skillTreeData.nodes.find(n => n.id === link.source))
            .filter((node): node is NonNullable<typeof node> => node !== undefined)
            .map(node => ({ id: node.id, name: node.name }));
    }

    /**
     * Gets child skills for a given skillId
     */
    getChildSkills(skillId: string | null): Array<{ id: string; name: string }> {
        if (!skillId) return [];
        return this.skillTreeData.links
            .filter(link => link.source === skillId)
            .map(link => this.skillTreeData.nodes.find(n => n.id === link.target))
            .filter((node): node is NonNullable<typeof node> => node !== undefined)
            .map(node => ({ id: node.id, name: node.name }));
    }

    /**
     * Gets the skill title for a given skillId
     */
    getSkillTitle(skillId: string | null): string {
        if (!skillId) return 'Select a skill leaf';
        const selectedSkillNode = this.skillTreeData.nodes.find(node => node.id === skillId);
        return selectedSkillNode?.name || 'Select a skill leaf';
    }

    /**
     * Generates a shareable link for a skill leaf (public view URL with short id in the path).
     */
    generateSkillLink(skillId: string): string {
        const origin =
            this.linkOrigin ??
            (typeof window !== 'undefined' ? window.location.origin : '');
        return buildTreeViewUrl(this.treeInteractor.getTreeId(), skillId, { origin });
    }

    /**
     * Whether shareable-link affordances (Copy Link, the section context menu, the
     * per-heading copy buttons) make sense for this tree. False only when the panel
     * was given an explicit `null` origin.
     */
    get canShareLinks(): boolean {
        return this.linkOrigin !== null;
    }

    /**
     * Full URL to copy for "link to this section" (skill view URL + document fragment).
     */
    buildSectionShareUrl(skillId: string, headingId: string): string {
        return `${this.generateSkillLink(skillId)}#${headingId}`;
    }

    /**
     * Renders markdown to the same HTML that the live skill view uses.
     *
     * Wired into SimpleMDE's `previewRender` so the in-editor preview matches the
     * production render (`leaf:` links, `video:yt:` embeds, heading anchors, etc.)
     * instead of SimpleMDE's default marked output.
     */
    renderPreviewHtml(markdown: string, skillId: string): string {
        return this.markdownRenderer.convertToSkillDescription(markdown, skillId).html;
    }

    /**
     * Decides whether the markdown editor should render as a plain textarea (mobile-friendly)
     * instead of the rich SimpleMDE editor. Delegates to `shouldUseTextareaEditor` in `skillPanelEditorPolicy.ts`.
     */
    shouldUseTextareaEditor(
        isNarrow: boolean,
        overrides?: skillPanelEditorPolicy.SkillPanelEditorOverrides,
    ): boolean {
        return skillPanelEditorPolicy.shouldUseTextareaEditor(isNarrow, overrides);
    }

    static leafHasLinkedGuide(skillTreeData: SkillData, skillId: string | null): boolean {
        return skillLeafHasLinkedGuide(skillTreeData, skillId);
    }
}

